import { _ as _to_array } from "@swc/helpers/_/_to_array";
import { _ as _to_consumable_array } from "@swc/helpers/_/_to_consumable_array";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { getUrlIdsFromNavigationPath } from "@faire/retailer-shared/categories/navigationPath";
import { NEW_PRODUCTS_CATEGORY_URL_ID } from "@faire/retailer-shared/categories/urlId";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { parseQueryParams } from "@faire/web--source/common/query-params/parseQueryParams";
import { useTraceComponentRender } from "@faire/web--source/common/tracing/useTraceComponentRender";
import { Button } from "@faire/web--source/slate/Button";
import { Arrow } from "@faire/web--source/slate/icons/foundation/Arrow";
import { Column, HorizontalLine } from "@faire/web--source/slate/Layout";
import { Spacer, getSpacing } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackRetailerOnboardingProductPageClickReturnToResultsClick } from "@faire/web-api--source/events/retailerOnboarding/click/productPageClickReturnToResults";
import { path as c1CategoryPath } from "@faire/web-api--source/routes/category/c1CategoryUrlId";
import productSearch from "@faire/web-api--source/routes/search";
import { observer } from "mobx-react";
import React from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { toUrlId } from "@faire/retailer/models/Category/IUrlId";
import { categoryPath } from "@faire/retailer/paths/category/categoryPath";
import { ProductPagePaths } from "@faire/retailer/paths/ProductPage";
import { useRetailerRoutingContext } from "@faire/retailer/RetailerRoutingContextProvider";
import { getCategoryDisplayName } from "@faire/retailer/services/Categories";
import { useCategoryStore } from "@faire/retailer/stores/domain/CategoryStore";
import { findClosestActiveCategoriesByUrlIds } from "@faire/retailer/stores/domain/CategoryStore/findByUrlId";
import { ProductBreadcrumbs } from "@faire/retailer/views/product/__internal__/ProductHeader/__internal__/ProductBreadcrumbs";
import { generateBrandUrlForProductPage } from "@faire/retailer/views/product/utils/generateBrandUrlForProductPage";
export var ProductHeader = function(param) {
    var brand = param.brand, product = param.product, hideBreadcrumbs = param.hideBreadcrumbs;
    useTraceComponentRender("ProductHeader");
    var location = useLocation();
    var backlinkQueryParam = parseQueryParams(location.search).get("backLink");
    var backlinkValue = typeof backlinkQueryParam === "string" ? decodeURIComponent(backlinkQueryParam) : undefined;
    if (backlinkValue) {
        return /*#__PURE__*/ _jsx(ProductReturnToResultsHeader, {
            backlink: backlinkValue,
            smallBottomMargin: hideBreadcrumbs
        });
    }
    if (hideBreadcrumbs) {
        return null;
    }
    var queryParams = ProductPagePaths.index.parseSearch(location.search);
    var brandParam = queryParams.refB;
    var searchParam = queryParams.refS;
    var categoriesParam = queryParams.refC;
    if (brandParam) {
        return /*#__PURE__*/ _jsx(ProductBrandBreadcrumbs, {
            brand: brand,
            product: product
        });
    } else if (searchParam) {
        var breadcrumbOptions = [
            {
                label: searchParam,
                path: productSearch({
                    q: searchParam
                })
            }
        ];
        return /*#__PURE__*/ _jsx(ProductBreadcrumbs, {
            product: product,
            breadcrumbOptions: breadcrumbOptions
        });
    } else if (categoriesParam) {
        return /*#__PURE__*/ _jsx(ProductCategoryBreadcrumbs, {
            product: product,
            navigationPathOrPathIdentifier: categoriesParam
        });
    } else {
        return /*#__PURE__*/ _jsx(ProductBrandBreadcrumbs, {
            brand: brand,
            product: product
        });
    }
};
var ProductReturnToResultsHeader = function(param) {
    var backlink = param.backlink, smallBottomMargin = param.smallBottomMargin;
    // extract string between first two / characters in the backlink
    var sourcePage = backlink.split(new RegExp("[/?]")).find(function(item) {
        return item !== "";
    });
    var isMobile = useViewport().isMobile;
    return /*#__PURE__*/ _jsxs(Column, {
        children: [
            /*#__PURE__*/ _jsxs(LeftAlignedButton, {
                variant: "tertiary",
                linkTo: backlink,
                $smallBottomMargin: smallBottomMargin,
                onClick: function() {
                    return trackRetailerOnboardingProductPageClickReturnToResultsClick(sourcePage !== null && sourcePage !== void 0 ? sourcePage : "unknown");
                },
                children: [
                    /*#__PURE__*/ _jsx(Arrow, {
                        direction: "left"
                    }),
                    /*#__PURE__*/ _jsx(Spacer, {
                        width: "1x"
                    }),
                    /*#__PURE__*/ _jsx(StrictLocalMsg, {
                        id: "ZKrZMY",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Back to results"
                            }
                        ]
                    })
                ]
            }),
            isMobile ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(HorizontalLine, {}),
                    /*#__PURE__*/ _jsx(Spacer, {
                        height: "5x"
                    })
                ]
            }) : null
        ]
    });
};
var LeftAlignedButton = styled(Button).withConfig({
    componentId: "sc-13f2654b-0"
})([
    "margin-top:",
    ";margin-bottom:",
    ";align-self:flex-start;white-space:nowrap;",
    "{margin-left:",
    ";}"
], getSpacing("2x"), function(props) {
    return props.$smallBottomMargin ? getSpacing("1x") : getSpacing("2x");
}, responsiveMediaQueries.mobile, getSpacing("2x"));
var ProductBrandBreadcrumbs = function(param) {
    var brand = param.brand, product = param.product;
    var _brand_name, _brand_token, _product_token;
    var breadcrumbsOptions = [
        {
            label: (_brand_name = brand.name) !== null && _brand_name !== void 0 ? _brand_name : "",
            path: generateBrandUrlForProductPage((_brand_token = brand.token) !== null && _brand_token !== void 0 ? _brand_token : "", (_product_token = product.token) !== null && _product_token !== void 0 ? _product_token : "")
        }
    ];
    return /*#__PURE__*/ _jsx(ProductBreadcrumbs, {
        product: product,
        breadcrumbOptions: breadcrumbsOptions
    });
};
var ProductCategoryBreadcrumbsBase = function(param) {
    var product = param.product, navigationPathOrPathIdentifier = param.navigationPathOrPathIdentifier;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var categories = useCategoryStore().categories;
    var routingContext = useRetailerRoutingContext();
    var _findClosestActiveCategoriesByUrlIds = _to_array(findClosestActiveCategoriesByUrlIds(getUrlIdsFromNavigationPath(navigationPathOrPathIdentifier), categories)), firstCategory = _findClosestActiveCategoriesByUrlIds[0], subCategories = _findClosestActiveCategoriesByUrlIds.slice(1);
    var categoryName = firstCategory === null || firstCategory === void 0 ? void 0 : firstCategory.name;
    var categoryUrlId = firstCategory === null || firstCategory === void 0 ? void 0 : firstCategory.url_id;
    var options = [];
    if (categoryName && categoryUrlId) {
        options.push({
            label: getCategoryDisplayName(categoryUrlId, strictLocalize),
            path: categoryUrlId === NEW_PRODUCTS_CATEGORY_URL_ID ? undefined : c1CategoryPath(categoryUrlId)
        });
        if (subCategories) {
            var subCategoryUrlIds = [];
            var _iteratorNormalCompletion = true, _didIteratorError = false, _iteratorError = undefined;
            try {
                for(var _iterator = subCategories[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true){
                    var subCategory = _step.value;
                    if (subCategory.url_id === undefined || subCategory.name === undefined) {
                        continue;
                    }
                    subCategoryUrlIds.push(toUrlId(subCategory.url_id));
                    options.push({
                        label: subCategory.name,
                        path: categoryPath({
                            urlIds: [
                                toUrlId(categoryUrlId)
                            ].concat(_to_consumable_array(subCategoryUrlIds)),
                            routingContext: routingContext
                        })
                    });
                }
            } catch (err) {
                _didIteratorError = true;
                _iteratorError = err;
            } finally{
                try {
                    if (!_iteratorNormalCompletion && _iterator.return != null) {
                        _iterator.return();
                    }
                } finally{
                    if (_didIteratorError) {
                        throw _iteratorError;
                    }
                }
            }
        }
    }
    return /*#__PURE__*/ _jsx(ProductBreadcrumbs, {
        product: product,
        breadcrumbOptions: options
    });
};
var ProductCategoryBreadcrumbs = observer(ProductCategoryBreadcrumbsBase);
